import axios from 'axios';
import { DataBody, BodyVolume, BrainField, Vertebra, Leg, LegNode, Lead, VertebraTemplate, LegNodeTemplate, CsvDownloadFile, CsvDownloadSummary, CsvFieldChoice, CsvFilterRule, Source, SourceInput, SourceDetail, Client, ClientInput, ClientDetail, ClientNodeAssignment } from '../types';
import { getStoredAccessToken, refreshTokens, clearTokens } from './auth';

const API_BASE = import.meta.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptor: attach access token to every request
api.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor: handle 401 by refreshing token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = await refreshTokens();
        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
        // For multipart uploads, the browser re-serializes the FormData on
        // replay with a NEW boundary. Any Content-Type carried over from the
        // first attempt pins the OLD boundary, so busboy parses zero files
        // ("No file received or file is empty"). Drop it so the browser sets
        // a fresh, matching multipart/form-data; boundary=... header.
        if (typeof FormData !== 'undefined' && originalRequest.data instanceof FormData) {
          delete originalRequest.headers['Content-Type'];
          delete originalRequest.headers['content-type'];
        }
        return api(originalRequest);
      } catch {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// --- Data Bodies ---

export async function getDataBodies(): Promise<DataBody[]> {
  const { data } = await api.get('/api/data-bodies');
  return data;
}

export async function getDataBody(id: string): Promise<DataBody> {
  const { data } = await api.get(`/api/data-bodies/${id}`);
  return data;
}

// ---------------------------------------------------------------
// Stats endpoints (deferred-load companions to the metadata calls
// above). These are intentionally separate HTTP calls so the
// metadata fetch can render the page immediately while the heavier
// COUNT-aggregation query continues server-side. Pages show
// skeleton placeholders in the stat cells until these resolve.
// ---------------------------------------------------------------

export type StatsRecord = Record<string, number>;

// Dashboard list: batch stats keyed by data_body_id. Returns
// { stats: { [id]: counts }, statsToday: { [id]: counts }, volume: { [id]: BodyVolume } }.
// Empty objects mean "this body has no leads yet" (not an error).
//
// `volume` is the rollup-backed set behind the Today / 7 Days / Gross
// columns and the delivery rate. It can come back empty on its own while
// `stats` succeeds (the server degrades the two independently), so callers
// must treat a missing volume entry as "not loaded", not as zero.
export async function getDataBodiesStats(): Promise<{
  stats: Record<string, StatsRecord>;
  statsToday: Record<string, StatsRecord>;
  volume: Record<string, BodyVolume>;
}> {
  const { data } = await api.get('/api/data-bodies/stats');
  return data;
}

// Body detail: { stats, statsToday } for a single body.
export async function getDataBodyStats(id: string): Promise<{
  stats: StatsRecord;
  statsToday: StatsRecord;
}> {
  const { data } = await api.get(`/api/data-bodies/${id}/stats`);
  return data;
}

export async function createDataBody(
  name: string,
  opts?: {
    dataRetentionDays?: number;
    rateLimitPerSecond?: number;
    concurrencyLimit?: number;
    /** Existing source picked from the combobox. */
    sourceId?: string;
    /** Name typed into the combobox that isn't a source yet — created server-side. */
    sourceName?: string;
  }
): Promise<DataBody> {
  const { data } = await api.post('/api/data-bodies', { name, ...opts });
  return data;
}

// --- Sources (who sends leads IN) ---
//
// Account-level, like suppressions: the list is shared by every data body in
// the account. Distinct from the reporting "Type" split (CSV/Webhook/API),
// which is how a lead arrived rather than who sent it.

export async function getSources(includeArchived = false): Promise<Source[]> {
  const { data } = await api.get(`/api/sources${includeArchived ? '?includeArchived=1' : ''}`);
  return data.sources;
}

export async function getSource(id: string): Promise<SourceDetail> {
  const { data } = await api.get(`/api/sources/${id}`);
  return data;
}

export async function createSource(input: SourceInput): Promise<Source> {
  const { data } = await api.post('/api/sources', input);
  return data;
}

export async function updateSource(
  id: string,
  updates: Partial<SourceInput> & { status?: 'active' | 'archived' }
): Promise<Source> {
  const { data } = await api.patch(`/api/sources/${id}`, updates);
  return data;
}

export async function deleteSource(id: string): Promise<{ deleted: boolean; detachedBodies: number }> {
  const { data } = await api.delete(`/api/sources/${id}`);
  return data;
}

// --- Clients (who receives leads OUT) ---
//
// Account-scoped, not scoped to a data body: one client commonly receives
// from several delivery nodes across several bodies. The outbound mirror of
// the sources block above. See migration 031.

export async function getClients(includeArchived = false): Promise<Client[]> {
  const { data } = await api.get(`/api/clients${includeArchived ? '?includeArchived=1' : ''}`);
  return data.clients;
}

export async function getClient(id: string): Promise<ClientDetail> {
  const { data } = await api.get(`/api/clients/${id}`);
  return data;
}

export async function createClient(input: ClientInput): Promise<Client> {
  const { data } = await api.post('/api/clients', input);
  return data;
}

export async function updateClient(
  id: string,
  updates: Partial<ClientInput> & { status?: 'active' | 'archived' }
): Promise<Client> {
  const { data } = await api.patch(`/api/clients/${id}`, updates);
  return data;
}

/**
 * Only succeeds while the client has never taken a delivery. Once it has,
 * the API returns 409 and the record must be archived instead — its
 * statistics are permanent.
 */
export async function deleteClient(id: string): Promise<{ deleted: boolean; detachedNodes: number }> {
  const { data } = await api.delete(`/api/clients/${id}`);
  return data;
}

// --- Client <-> leg-node assignment ---

/** The node's current client, plus whether the assignment is locked. */
export async function getLegNodeClient(
  dataBodyId: string,
  nodeId: string
): Promise<{ assignment: ClientNodeAssignment | null; client: Client | null }> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/client`);
  return data;
}

/**
 * Assign or re-assign the receiving client. Pass `clientId` to pick an
 * existing one or `clientName` to create one on the fly.
 *
 * Throws 409 once the node has delivered a lead — at that point the
 * assignment is frozen for the life of the node.
 */
export async function setLegNodeClient(
  dataBodyId: string,
  nodeId: string,
  selection: { clientId?: string; clientName?: string }
): Promise<ClientNodeAssignment> {
  const { data } = await api.put(
    `/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/client`,
    selection
  );
  return data;
}


export async function updateDataBody(id: string, updates: Partial<DataBody>): Promise<DataBody> {
  const { data } = await api.patch(`/api/data-bodies/${id}`, updates);
  return data;
}

export async function deleteDataBody(id: string): Promise<void> {
  await api.delete(`/api/data-bodies/${id}`);
}

// --- Brain Fields ---

export async function getBrainFields(dataBodyId: string): Promise<BrainField[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/brain-fields`);
  return data;
}

export async function createBrainField(dataBodyId: string, field: { fieldName: string; fieldLabel: string; fieldType?: string; isRequired?: boolean }): Promise<BrainField> {
  const { data } = await api.post(`/api/data-bodies/${dataBodyId}/brain-fields`, field);
  return data;
}

export async function updateBrainField(dataBodyId: string, fieldId: string, updates: Partial<BrainField>): Promise<BrainField> {
  const { data } = await api.patch(`/api/data-bodies/${dataBodyId}/brain-fields/${fieldId}`, updates);
  return data;
}

export async function deleteBrainField(dataBodyId: string, fieldId: string): Promise<void> {
  await api.delete(`/api/data-bodies/${dataBodyId}/brain-fields/${fieldId}`);
}

// --- Vertebrae ---

export async function getVertebrae(dataBodyId: string): Promise<Vertebra[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/vertebrae`);
  return data;
}

export async function createVertebra(dataBodyId: string, vertebra: { type: string; name: string; config?: any; onFail?: string }): Promise<Vertebra> {
  const { data } = await api.post(`/api/data-bodies/${dataBodyId}/vertebrae`, vertebra);
  return data;
}

export async function updateVertebra(dataBodyId: string, vertebraId: string, updates: Partial<Vertebra>): Promise<Vertebra> {
  const { data } = await api.patch(`/api/data-bodies/${dataBodyId}/vertebrae/${vertebraId}`, updates);
  return data;
}

export async function reorderVertebrae(dataBodyId: string, orderedIds: string[]): Promise<Vertebra[]> {
  const { data } = await api.put(`/api/data-bodies/${dataBodyId}/vertebrae/reorder`, { orderedIds });
  return data;
}

export async function deleteVertebra(dataBodyId: string, vertebraId: string): Promise<void> {
  await api.delete(`/api/data-bodies/${dataBodyId}/vertebrae/${vertebraId}`);
}

// --- Legs (named containers — since migration 005) ---
// Each data body can have multiple legs. Each leg holds its own decision
// tree of leg-nodes. A lead going past the spine is attempted on every
// active, non-empty leg in parallel.

export async function getLegs(dataBodyId: string): Promise<Leg[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/legs`);
  return data;
}

export async function createLeg(dataBodyId: string, payload: { name: string; isActive?: boolean }): Promise<Leg> {
  const { data } = await api.post(`/api/data-bodies/${dataBodyId}/legs`, payload);
  return data;
}

export async function updateLeg(dataBodyId: string, legId: string, updates: Partial<Pick<Leg, 'name' | 'isActive' | 'position'>>): Promise<Leg> {
  const { data } = await api.patch(`/api/data-bodies/${dataBodyId}/legs/${legId}`, updates);
  return data;
}

export async function deleteLeg(dataBodyId: string, legId: string): Promise<void> {
  await api.delete(`/api/data-bodies/${dataBodyId}/legs/${legId}`);
}

// Deep-copy a leg (container + full node tree). Omit targetDataBodyId (or pass
// the same body) for "Copy Leg In"; pass another body's id for "Copy Leg Out".
// The copy is always returned INACTIVE. Response bundles the new leg plus every
// node inside it so the caller can update local state without a full refetch.
export async function cloneLeg(
  dataBodyId: string,
  legId: string,
  opts?: { targetDataBodyId?: string; name?: string }
): Promise<{ leg: Leg; legNodes: LegNode[] }> {
  const { data } = await api.post(`/api/data-bodies/${dataBodyId}/legs/${legId}/clone`, opts || {});
  return data;
}

export async function reorderLegs(dataBodyId: string, orderedIds: string[]): Promise<Leg[]> {
  const { data } = await api.put(`/api/data-bodies/${dataBodyId}/legs/reorder`, { orderedIds });
  return data;
}

// Lifetime per-leg pass/fail counts (one increment per lead per leg).
export async function getLegStats(dataBodyId: string, legId: string): Promise<{ pass: number; fail: number }> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/legs/${legId}/stats`);
  return data;
}

// --- Leg Nodes ---
// Renamed from /legs/:nodeId in migration 005 to disambiguate from the new
// leg-entity routes above. Existing UI behavior is unchanged.

export async function getLegNodes(dataBodyId: string): Promise<LegNode[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leg-nodes`);
  return data;
}

export async function createLegNode(
  dataBodyId: string,
  node: {
    type: string;
    name: string;
    parentNodeId?: string;
    config?: any;
    legId?: string;
    /** Required for delivery node types (migration 031). Pass one or the
     *  other: an existing client's id, or a name to create on the fly. */
    clientId?: string;
    clientName?: string;
  }
): Promise<LegNode> {
  // legId is optional in Round 1 — backend auto-targets the data body's first
  // (or newly-created default) leg if omitted. Round 2 multi-leg UI passes it.
  const { data } = await api.post(`/api/data-bodies/${dataBodyId}/leg-nodes`, node);
  return data;
}

export async function updateLegNode(dataBodyId: string, nodeId: string, updates: Partial<LegNode>): Promise<LegNode> {
  const { data } = await api.patch(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}`, updates);
  return data;
}

export async function deleteLegNode(dataBodyId: string, nodeId: string): Promise<void> {
  await api.delete(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}`);
}

// --- Leads ---

export async function getLeads(dataBodyId: string, opts?: { status?: string; limit?: number; offset?: number }): Promise<Lead[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leads`, { params: opts });
  return data;
}

// Throttled-hit counters (rate-limit + concurrency rejections). These are
// dropped at the door without a leads row, so they never appear in getLeads;
// this surfaces the per-day Redis counters instead. Buckets are UTC days.
export interface RejectionBucket {
  today: number;
  last7Days: number;
  byDay: Array<{ date: string; count: number }>;
}
export interface RejectionSummary {
  windowDays: number;
  bucket: string;
  rateLimit: RejectionBucket;
  concurrency: RejectionBucket;
}
export async function getRejections(dataBodyId: string): Promise<RejectionSummary> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/rejections`);
  return data;
}

// Lifetime pass/fail counts for a single vertebra.
export async function getVertebraStats(dataBodyId: string, vertebraId: string): Promise<{ pass: number; fail: number }> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/vertebrae/${vertebraId}/stats`);
  return data;
}

// Most recent N leads that passed or failed at a specific vertebra.
export async function getVertebraLeads(
  dataBodyId: string,
  vertebraId: string,
  status: 'pass' | 'fail',
  limit = 20
): Promise<Lead[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/vertebrae/${vertebraId}/leads`, {
    params: { status, limit },
  });
  return data;
}

// Lifetime pass/fail counts for a single leg node (renamed from getLegStats).
export async function getLegNodeStats(dataBodyId: string, nodeId: string): Promise<{ pass: number; fail: number }> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/stats`);
  return data;
}

// Most recent N leads that passed or failed at a specific leg node
// (renamed from getLegLeads).
export async function getLegNodeLeads(
  dataBodyId: string,
  nodeId: string,
  status: 'pass' | 'fail',
  limit = 20
): Promise<Lead[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/leads`, {
    params: { status, limit },
  });
  return data;
}

export async function getLead(dataBodyId: string, leadId: string): Promise<Lead> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leads/${leadId}`);
  return data;
}

// --- Outbound API Log (Custom API / webhook legs) ---

export interface DispatchLog {
  dataBodyId: string;
  leadId: string;
  legNodeId: string;
  legId: string | null;
  category: string;
  statusCode: number | null;
  latencyMs: number | null;
  wasRetried: boolean;
  requestUrl: string | null;
  requestHeaders: Record<string, string> | null;
  requestBody: string | null;
  bodyFormat: string | null;
  responseBody: string | null;
  message: string | null;
  attemptedAt: string;
  expiresAt: string;
}

export async function getLegNodeDispatchLog(
  dataBodyId: string,
  nodeId: string,
  leadId: string
): Promise<DispatchLog> {
  const { data } = await api.get(
    `/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/leads/${leadId}/dispatch-log`
  );
  return data;
}

// --- Test A Lead (Custom API / Webhook Output legs) ---
// Mirrors the backend DispatchResult. Runs one synthetic lead through the real
// outbound path so the user can confirm the destination accepts the data
// before turning the feed on. No dispatch-log row or counters are touched.
export interface TestLeadResult {
  success: boolean;
  skipped?: boolean;
  message: string;
  category?: string;
  statusCode?: number | null;
  requestUrl?: string | null;
  requestHeaders?: Record<string, string> | null;
  requestBody?: string | null;
  bodyFormat?: string | null;
  responseBody?: string | null;
}

export async function testLegNode(
  dataBodyId: string,
  nodeId: string,
  payload: { config?: any; testData?: Record<string, any> }
): Promise<TestLeadResult> {
  const { data } = await api.post(
    `/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/test`,
    payload
  );
  return data;
}

// --- Email Oversight test ---

export async function testEmailOversight(
  dataBodyId: string,
  apiToken: string,
  listId: number | string,
  email: string
): Promise<{ httpStatus: number; response: any }> {
  const { data } = await api.post(`/api/data-bodies/${dataBodyId}/test-email-oversight`, {
    apiToken, listId, email,
  });
  return data;
}

// --- System info ---

// Server's egress IP — the IP that third-party APIs (like Email Oversight)
// will see when our backend calls them. Used by the EO vertebra config UI
// to tell the user which IP to whitelist.
export async function getEgressIp(): Promise<{ ip: string; cached: boolean }> {
  const { data } = await api.get('/api/system/egress-ip');
  return data;
}

// --- Templates ---

export async function getVertebraeTemplates(): Promise<VertebraTemplate[]> {
  const { data } = await api.get('/api/data-bodies/templates/vertebrae');
  return data;
}

export async function getLegNodeTemplates(): Promise<LegNodeTemplate[]> {
  const { data } = await api.get('/api/data-bodies/templates/legs');
  return data;
}

// --- CSV Bulk Import ---
// Three calls back the bulk-CSV-upload modal in BrainSection. Upload uses
// FormData (multipart) so the file streams to disk on the backend; the
// other two are tiny JSON polls.

export interface CsvImportStatus {
  id: string;
  dataBodyId: string;
  filename: string;
  fileSize: number;
  status: 'queued' | 'processing' | 'completed' | 'canceled' | 'failed';
  processedRows: number;
  succeededRows: number;
  failedRows: number;
  totalRows: number | null;
  gapStopAtRow: number | null;
  hasRejectedFile: boolean;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  canceledAt: string | null;
}

export interface CsvImportLimits {
  maxUploadBytes: number;
  blankRowGapStop: number;
}

export async function getCsvImportLimits(): Promise<CsvImportLimits> {
  const { data } = await api.get('/api/imports/config/limits');
  return data;
}

/**
 * Stream-upload a CSV with the user's column mapping. Calls the
 * onProgress callback with bytes-uploaded / bytes-total during the
 * transfer (uses XHR upload events under the hood). Returns the
 * server-assigned jobId once the upload is accepted.
 */
export async function uploadCsv(
  file: File,
  dataBodyId: string,
  mapping: Record<string, 'import' | 'ignore'>,
  onProgress?: (loaded: number, total: number) => void
): Promise<{ jobId: string }> {
  const form = new FormData();
  form.append('dataBodyId', dataBodyId);
  form.append('mapping', JSON.stringify(mapping));
  form.append('file', file, file.name);

  const { data } = await api.post('/api/imports/csv', form, {
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {onProgress(evt.loaded, evt.total);}
    },
    // Pass undefined (not 'multipart/form-data') so axios/the browser set
    // the full header WITH a boundary. Hard-coding it omits the boundary
    // and busboy parses zero files.
    headers: { 'Content-Type': undefined },
    // Big files: don't let axios timeout the upload.
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });
  return data;
}

export async function getCsvImportStatus(jobId: string): Promise<CsvImportStatus> {
  const { data } = await api.get(`/api/imports/${jobId}`);
  return data;
}

export async function cancelCsvImportJob(jobId: string): Promise<void> {
  await api.post(`/api/imports/${jobId}/cancel`);
}

/**
 * URL for downloading the rejected-rows CSV. The browser navigates
 * to this directly so the auth header is included via cookie/bearer.
 * Returns the absolute URL with the bearer token query param appended,
 * since browser <a download> won't carry the Authorization header.
 */
export function csvImportRejectedUrl(jobId: string): string {
  // Backend's /api/imports/:id/rejected requires the auth middleware,
  // which only reads Bearer tokens. To make a plain <a download> work
  // we'd need either a presigned URL or a cookie. Simpler: fetch the
  // file via the api client (which sends Bearer), then turn the blob
  // into a transient object URL. See downloadCsvImportRejected below.
  return `${API_BASE}/api/imports/${jobId}/rejected`;
}

export async function downloadCsvImportRejected(jobId: string, filename = 'rejected.csv'): Promise<void> {
  const resp = await api.get(`/api/imports/${jobId}/rejected`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(resp.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------
// CSV Download (manual-export legs — migration 014)
// ---------------------------------------------------------------
// All endpoints scoped to a leg-node of type csv_download. The user
// configures filters in the modal, hits Download (summary), reviews
// the breakdown, then Finalize creates the file row + enqueues the
// export job. /files polls progress; /download-url mints a signed R2 URL.

export interface CsvDownloadStats {
  filesCount: number;
  rowsTotal: number;
  lastDownloadAt: string | null;
  uniqueLeadsTracked: number;
}

export interface CsvDownloadConfigInput {
  totalDesired?: number;
  dateRangeFrom?: string | null;
  dateRangeTo?: string | null;
  fields: CsvFieldChoice[];
  filterRules: CsvFilterRule[];
  unique?: boolean;
}

export async function getCsvDownloadStats(dataBodyId: string, nodeId: string): Promise<CsvDownloadStats> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/csv-download/${nodeId}/stats`);
  return data;
}

export async function getCsvDownloadFiles(dataBodyId: string, nodeId: string): Promise<CsvDownloadFile[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/csv-download/${nodeId}/files`);
  return data;
}

// ---------------------------------------------------------------
// Cloud Data Destination (batch bucket file-drop legs — migration 026)
// ---------------------------------------------------------------
export interface CloudDestRun {
  id: string;
  partitionDate: string;
  provider: string;
  bucket: string | null;
  objectKey: string | null;
  fileSizeBytes: number | null;
  rowCount: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export async function getCloudDestRuns(dataBodyId: string, nodeId: string): Promise<CloudDestRun[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/cloud-dest-runs`);
  return data;
}

// --- SFTP / FTP delivery leg ---------------------------------------------

export interface SftpDeliveryRun {
  id: string;
  partitionDate: string;
  protocol: string;
  host: string | null;
  remotePath: string | null;
  fileSizeBytes: number | null;
  rowCount: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface SftpHealth {
  status: 'ok' | 'error' | 'unknown';
  latencyMs?: number | null;
  error?: string | null;
  checkedAt?: string;
  lastOkAt?: string | null;
}

export interface SftpTestResult {
  ok: boolean;
  latencyMs: number;
  error?: string;
}

export async function getSftpRuns(dataBodyId: string, nodeId: string): Promise<SftpDeliveryRun[]> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/sftp-runs`);
  return data;
}

export async function getSftpHealth(dataBodyId: string, nodeId: string): Promise<SftpHealth> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId}/sftp-health`);
  return data;
}

// Test an SFTP/FTP connection. `nodeId` is 'new' for an unsaved node (all
// credentials must then be present in `sftp`). For a saved node, blank/masked
// credential fields fall back to the stored sealed values on the server.
export async function testSftpConnection(dataBodyId: string, nodeId: string, sftp: any): Promise<SftpTestResult> {
  const { data } = await api.post(`/api/data-bodies/${dataBodyId}/leg-nodes/${nodeId || 'new'}/sftp-test`, { sftp });
  return data;
}

// The shared service-account email a client shares their Google Sheet with, for
// the google_sheets leg config modal. `configured` is false if the deployment
// has no GOOGLE_SHEETS_SA_KEY set.
export async function getGoogleSheetsServiceAccount(): Promise<{ configured: boolean; email: string | null }> {
  const { data } = await api.get('/api/data-bodies/google-sheets/service-account');
  return data;
}

export async function getCsvDownloadFile(dataBodyId: string, nodeId: string, fileId: string): Promise<CsvDownloadFile> {
  const { data } = await api.get(`/api/data-bodies/${dataBodyId}/csv-download/${nodeId}/files/${fileId}`);
  return data;
}

export async function getCsvDownloadSummary(
  dataBodyId: string,
  nodeId: string,
  cfg: CsvDownloadConfigInput
): Promise<CsvDownloadSummary & { maxRows: number; maxFileBytes: number; softWarnRows: number }> {
  const { data } = await api.post(
    `/api/data-bodies/${dataBodyId}/csv-download/${nodeId}/summary`,
    cfg
  );
  return data;
}

export async function finalizeCsvDownload(
  dataBodyId: string,
  nodeId: string,
  cfg: CsvDownloadConfigInput,
  summary: CsvDownloadSummary | null
): Promise<{ fileId: string; status: string }> {
  const { data } = await api.post(
    `/api/data-bodies/${dataBodyId}/csv-download/${nodeId}/finalize`,
    { config: cfg, summary }
  );
  return data;
}

export async function mintCsvDownloadUrl(dataBodyId: string, nodeId: string, fileId: string): Promise<{ url: string; expiresInSeconds: number }> {
  const { data } = await api.post(
    `/api/data-bodies/${dataBodyId}/csv-download/${nodeId}/files/${fileId}/download-url`
  );
  return data;
}

export async function deleteCsvDownloadFile(dataBodyId: string, nodeId: string, fileId: string): Promise<void> {
  await api.delete(`/api/data-bodies/${dataBodyId}/csv-download/${nodeId}/files/${fileId}`);
}

// ---------------------------------------------------------------
// Suppression lists (account-scoped — migration 015)
// ---------------------------------------------------------------
// Used by the email_suppression / phone_suppression vertebra shells.
// All endpoints are scoped to the authenticated user's account (org or
// solo). The same list is shared across every data body in the account.

export type SuppressionType = 'email' | 'phone';

export interface SuppressionEntry {
  id: number;
  type: SuppressionType;
  valueNorm: string;
  source: string | null;
  createdAt: string;
}

export interface SuppressionCounts {
  email: number;
  phone: number;
}

export interface SuppressionImportJob {
  id: string;
  type: SuppressionType;
  filename: string | null;
  fileSizeBytes: number | null;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  rowsTotal: number | null;
  rowsProcessed: number;
  rowsInserted: number;
  rowsDuplicate: number;
  rowsRejected: number;
  progressPct: number;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export async function getSuppressionCounts(): Promise<{ counts: SuppressionCounts; softWarnTotal: number }> {
  const { data } = await api.get('/api/suppressions/counts');
  return data;
}

export async function listSuppressionEntries(
  type: SuppressionType,
  opts: { limit?: number; offset?: number; search?: string } = {}
): Promise<SuppressionEntry[]> {
  const params: any = { type, ...opts };
  const { data } = await api.get('/api/suppressions', { params });
  return data.entries;
}

export async function pasteSuppressionEntries(
  type: SuppressionType,
  values: string | string[]
): Promise<{ inserted: number; duplicate: number; rejected: number; submitted: number }> {
  const { data } = await api.post('/api/suppressions/paste', { type, values });
  return data;
}

export async function uploadSuppressionCsv(
  type: SuppressionType,
  file: File,
  onProgress?: (loaded: number, total: number) => void
): Promise<{ jobId: string }> {
  const form = new FormData();
  form.append('type', type);
  form.append('file', file, file.name);
  const { data } = await api.post('/api/suppressions/csv', form, {
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {onProgress(evt.loaded, evt.total);}
    },
    headers: { 'Content-Type': undefined },
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });
  return data;
}

export async function getSuppressionImportStatus(jobId: string): Promise<SuppressionImportJob> {
  const { data } = await api.get(`/api/suppressions/imports/${jobId}`);
  return data.job;
}

export async function deleteSuppressionEntry(id: number): Promise<void> {
  await api.delete(`/api/suppressions/${id}`);
}

// ---------------------------------------------------------------
// Reporting (migration 017 — report_rollup_hourly / _daily)
// ---------------------------------------------------------------
// Every endpoint is account-scoped on the backend; no caller-supplied
// account id needed. Date ranges are ISO timestamps; backend defaults to
// "last 7 days" if either side is missing.

export type ReportingFreshness = 'fresh' | 'stale' | 'cold' | 'bypass';
export type ReportingGranularity = 'hour' | 'day';

export interface ReportingOverviewTotals {
  leadsIn: number;
  passedSpine: number;
  failed: number;
  doorRejected: number;
  passRate: number;
}

export interface ReportingSourceRow {
  source: string;
  accepts: number;
  rejects: number;
}

export interface ReportingTimeSeriesPoint {
  bucket: string;
  accepts: number;
  rejects: number;
}

export interface ReportingTimeSeries {
  granularity: ReportingGranularity;
  series: ReportingTimeSeriesPoint[];
}

export interface ReportingBodyRow {
  dataBodyId: string;
  dataBodyName: string | null;
  /** Who sends leads to this body. Null for bodies with no source assigned. */
  sourceId: string | null;
  sourceName: string | null;
  accepts: number;
  rejects: number;
  acceptRate: number;
}

/** Sentinel id for the "no source assigned" bucket. Matches the backend. */
export const UNASSIGNED_SOURCE = 'unassigned';

/** One row per SOURCE, rolled up across every data body that source owns. */
export interface ReportingSourceCompanyRow {
  sourceId: string;
  sourceName: string | null;
  bodyCount: number;
  accepts: number;
  rejects: number;
  acceptRate: number;
}

export interface ReportingSourcesResponse {
  from: string;
  to: string;
  freshness: ReportingFreshness;
  sources: ReportingSourceCompanyRow[];
}

export interface ReportingNodeRow {
  nodeKind: 'brain' | 'vertebra' | 'leg' | 'leg_node';
  nodeId: string;
  nodeName: string | null;
  nodeType: string | null;
  legId: string | null;
  position: number | null;
  accepts: number;
  rejects: number;
  acceptRate: number;
}

/**
 * The four KPI cards, in the DASHBOARD's vocabulary rather than the
 * rollup's. Deliberately identical wording and derivation to the dashboard
 * volume columns so a number on both screens agrees on both screens.
 *
 * `today` and `gross` are FIXED windows — the date-range picker does not
 * move them. `range` is the one the picker drives, which is why the UI
 * relabels its header to match the preset ("7 DAYS", "30 DAYS", …).
 *
 * `deliveryRate` is a FAN-OUT RATIO, not a percentage of a whole: one
 * accepted lead sent to three destinations is 300%. Over 100% is normal.
 * Never clamp it. Null (not 0) when nothing came in — "0%" would read as a
 * total delivery failure.
 */
export interface ReportingVolume {
  today: number;
  range: number;
  gross: number;
  deliveredInRange: number;
  deliveryRate: number | null;
}

export interface ReportingOverviewResponse {
  from: string;
  to: string;
  freshness: ReportingFreshness;
  overview: ReportingOverviewTotals;
  sources: ReportingSourceRow[];
  series: ReportingTimeSeries;
  volume: ReportingVolume;
}

/** One row of the "By data body" tab. Inbound; rate is fan-out aware. */
export interface ReportingBodyVolumeRow {
  dataBodyId: string;
  dataBodyName: string | null;
  sourceId: string | null;
  sourceName: string | null;
  today: number;
  range: number;
  gross: number;
  deliveredInRange: number;
  /** Ratio, may exceed 1. Null when nothing came in over the range. */
  deliveryRate: number | null;
}

export interface ReportingBodyVolumeResponse {
  from: string;
  to: string;
  freshness: ReportingFreshness;
  bodies: ReportingBodyVolumeRow[];
}

/**
 * One row of the "By leg" tab — a delivery DESTINATION (leg-node), which
 * is the only level that has a client and the only level where "accept
 * rate" is a true 0..1 percentage. Do not confuse it with the body-level
 * delivery rate, which is a fan-out ratio.
 */
export interface ReportingLegVolumeRow {
  legNodeId: string;
  nodeName: string | null;
  nodeType: string | null;
  legId: string | null;
  legName: string | null;
  dataBodyId: string | null;
  dataBodyName: string | null;
  clientId: string;
  clientName: string | null;
  today: number;
  range: number;
  gross: number;
  failedInRange: number;
  acceptRate: number | null;
  /** The destination (or its leg/body) has been deleted. Stats are kept. */
  detached: boolean;
}

export interface ReportingLegsResponse {
  from: string;
  to: string;
  freshness: ReportingFreshness;
  legs: ReportingLegVolumeRow[];
}

export interface ReportingLegResponse {
  from: string;
  to: string;
  legNodeId: string;
  freshness: ReportingFreshness;
  leg: ReportingLegVolumeRow;
  series: ReportingTimeSeries;
}

export interface ReportingBodiesResponse {
  from: string;
  to: string;
  freshness: ReportingFreshness;
  bodies: ReportingBodyRow[];
}

export interface ReportingDoorRejectRow {
  /** 'rate_limit' | 'concurrency' | 'required_field' | '' (unclassified). */
  reason: string;
  rejects: number;
}

export interface ReportingBodyResponse {
  from: string;
  to: string;
  dataBodyId: string;
  freshness: ReportingFreshness;
  overview: ReportingOverviewTotals;
  series: ReportingTimeSeries;
  nodes: ReportingNodeRow[];
  /** Brain "door reject" total broken down by reason. May be empty. */
  doorRejects: ReportingDoorRejectRow[];
}

/**
 * GET /api/reports/sources/:id — everything happening inside one source,
 * rolled up across every data body that source owns.
 */
export interface ReportingSourceResponse {
  from: string;
  to: string;
  sourceId: string;
  /** The directory record. Null for the 'unassigned' bucket. */
  source: Source | null;
  freshness: ReportingFreshness;
  overview: ReportingOverviewTotals;
  series: ReportingTimeSeries;
  /** Ingest channel split (CSV / Webhook / API) for this source. */
  types: ReportingSourceRow[];
  bodies: ReportingBodyRow[];
  doorRejects: ReportingDoorRejectRow[];
}

/** Sentinel id for the "no client assigned" bucket. Matches the backend. */
export const UNASSIGNED_CLIENT = 'unassigned';

/**
 * One row per CLIENT — who we deliver TO — rolled up across every delivery
 * node assigned to them, deleted nodes included.
 *
 * Note the vocabulary shift from the source tables: on the outbound side an
 * "accept" is a DELIVERED lead and a "reject" is a FAILED delivery, so the
 * fields are named for what they actually mean to a buyer relationship.
 */
export interface ReportingClientRow {
  clientId: string;
  clientName: string | null;
  nodeCount: number;
  bodyCount: number;
  delivered: number;
  failed: number;
  deliveryRate: number;
}

export interface ReportingClientsResponse {
  from: string;
  to: string;
  freshness: ReportingFreshness;
  clients: ReportingClientRow[];
}

export interface ReportingClientOverview {
  delivered: number;
  failed: number;
  attempted: number;
  deliveryRate: number;
  destinations: number;
  bodies: number;
}

/** One destination (leg-node) inside a client drilldown. */
export interface ReportingClientNodeRow {
  legNodeId: string;
  nodeName: string | null;
  nodeType: string | null;
  dataBodyId: string | null;
  dataBodyName: string | null;
  legName: string | null;
  delivered: number;
  failed: number;
  deliveryRate: number;
  /** The node (or its leg/body) has been deleted. Its stats are kept. */
  detached: boolean;
  /** The node has delivered, so its client can no longer be changed. */
  locked: boolean;
}

/** One data body feeding a client, with that body's own inbound source. */
export interface ReportingClientBodyRow {
  dataBodyId: string;
  dataBodyName: string | null;
  sourceId: string | null;
  sourceName: string | null;
  delivered: number;
  failed: number;
  deliveryRate: number;
}

/**
 * GET /api/reports/clients/:id — everything we have ever sent one buyer,
 * rolled up across every destination assigned to them.
 */
export interface ReportingClientResponse {
  from: string;
  to: string;
  clientId: string;
  /** The directory record. Null for the 'unassigned' bucket. */
  client: Client | null;
  /** Every node ever wired to this client, live and detached. */
  assignments: ClientNodeAssignment[];
  freshness: ReportingFreshness;
  overview: ReportingClientOverview;
  series: ReportingTimeSeries;
  nodes: ReportingClientNodeRow[];
  bodies: ReportingClientBodyRow[];
}

function reportParams(
  from: Date,
  to: Date,
  fresh?: boolean,
  /** Counterparty filter — a Source id, or 'unassigned'. Omitted = whole account. */
  sourceId?: string
): URLSearchParams {
  const p = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  });
  if (fresh) {p.set('fresh', '1');}
  if (sourceId) {p.set('sourceId', sourceId);}
  return p;
}

export async function getReportingOverview(from: Date, to: Date, fresh = false, sourceId?: string): Promise<ReportingOverviewResponse> {
  const { data } = await api.get(`/api/reports/overview?${reportParams(from, to, fresh, sourceId)}`);
  return data;
}

export async function getReportingBodies(from: Date, to: Date, fresh = false, sourceId?: string): Promise<ReportingBodiesResponse> {
  const { data } = await api.get(`/api/reports/bodies?${reportParams(from, to, fresh, sourceId)}`);
  return data;
}

/** One row per source, across every body it owns. Powers the "By source" table. */
export async function getReportingSources(from: Date, to: Date, fresh = false): Promise<ReportingSourcesResponse> {
  const { data } = await api.get(`/api/reports/sources?${reportParams(from, to, fresh)}`);
  return data;
}

/** The full "everything happening inside this source" drilldown payload. */
export async function getReportingSource(id: string, from: Date, to: Date, fresh = false): Promise<ReportingSourceResponse> {
  const { data } = await api.get(`/api/reports/sources/${id}?${reportParams(from, to, fresh)}`);
  return data;
}

/** One row per client, across every destination they receive from. */
export async function getReportingClients(from: Date, to: Date, fresh = false): Promise<ReportingClientsResponse> {
  const { data } = await api.get(`/api/reports/clients?${reportParams(from, to, fresh)}`);
  return data;
}

/** The full "everything we have sent this buyer" drilldown payload. */
export async function getReportingClient(id: string, from: Date, to: Date, fresh = false): Promise<ReportingClientResponse> {
  const { data } = await api.get(`/api/reports/clients/${id}?${reportParams(from, to, fresh)}`);
  return data;
}

export async function getReportingBody(id: string, from: Date, to: Date, fresh = false): Promise<ReportingBodyResponse> {
  const { data } = await api.get(`/api/reports/bodies/${id}?${reportParams(from, to, fresh)}`);
  return data;
}

/** "By data body" tab rows — Today / Range / Gross + fan-out delivery rate. */
export async function getReportingBodyVolume(
  from: Date, to: Date, fresh = false, sourceId?: string
): Promise<ReportingBodyVolumeResponse> {
  const { data } = await api.get(`/api/reports/volume/bodies?${reportParams(from, to, fresh, sourceId)}`);
  return data;
}

/**
 * "By leg" tab rows — one per delivery destination. Takes BOTH filters:
 * sourceId narrows to destinations inside bodies fed by that source,
 * clientId narrows to one buyer's destinations.
 */
export async function getReportingLegs(
  from: Date, to: Date, fresh = false, sourceId?: string, clientId?: string
): Promise<ReportingLegsResponse> {
  const p = reportParams(from, to, fresh, sourceId);
  if (clientId) {p.set('clientId', clientId);}
  const { data } = await api.get(`/api/reports/volume/legs?${p}`);
  return data;
}

/** One delivery destination: its four cards plus volume over time. */
export async function getReportingLeg(
  id: string, from: Date, to: Date, fresh = false
): Promise<ReportingLegResponse> {
  const { data } = await api.get(`/api/reports/legs/${id}?${reportParams(from, to, fresh)}`);
  return data;
}
